import React, { useState, useEffect } from 'react';
import { createTicket, getTickets, getTicketDetails } from '../services/api.js';
import { useToast } from './Toast';
import { LoadingButton } from './LoadingSpinner';

export default function TicketSystem({ theme = 'light' }) {
  const [tickets, setTickets] = useState([]);
  const [newTicket, setNewTicket] = useState({ 
    subject: '', 
    message: '', 
    priority: 'medium' 
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [backendAvailable, setBackendAvailable] = useState(true);
  const { showSuccess, showError, ToastContainer } = useToast();

  // Fetch existing tickets on component mount
  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setInitialLoading(true);
      const response = await getTickets();
      // Backend returns array directly, not wrapped in tickets property
      setTickets(Array.isArray(response) ? response : (response.tickets || []));
      setBackendAvailable(true);
    } catch (err) {
      console.error('Failed to load tickets:', err);
      // Check if it's a backend connection issue
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError') || 
          err.message.includes('CORS') || err.message.includes('500')) {
        setBackendAvailable(false);
        showError('Support system is temporarily unavailable. Please check your internet connection.');
      } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        showError('Please log in to view your support tickets.');
      } else {
        showError('Failed to load tickets. Please try again later.');
      }
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await createTicket(newTicket);
      setNewTicket({ subject: '', message: '', priority: 'medium' });
      setBackendAvailable(true);
      showSuccess('Ticket submitted successfully! We\'ll respond within 24-48 hours.');
      loadTickets(); // Refresh tickets list
    } catch (err) {
      console.error('Failed to submit ticket:', err);
      // Check if it's a CORS or backend connection issue
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setBackendAvailable(false);
        showError('Unable to connect to support system. Please check your internet connection and try again.');
      } else if (err.message.includes('CORS') || err.message.includes('Access-Control-Allow-Origin')) {
        setBackendAvailable(false);
        showError('Service temporarily unavailable. Please try again later or contact support directly.');
      } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        showError('Please log in to submit a support ticket.');
      } else if (err.message.includes('400') || err.message.includes('Bad Request')) {
        showError('Please check that all required fields are filled out correctly.');
      } else if (err.message.includes('500') || err.message.includes('Internal Server Error')) {
        setBackendAvailable(false);
        showError('Support system is temporarily unavailable. Please try again later.');
      } else {
        showError('Failed to submit ticket. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTicketClick = async (ticket) => {
    try {
      // Use the correct ID field from backend response
      const ticketId = ticket.id || ticket._id;
      const ticketDetails = await getTicketDetails(ticketId);
      setSelectedTicket(ticketDetails);
      setShowTicketDetail(true);
    } catch (err) {
      console.error('Failed to load ticket details:', err);
      if (err.message.includes('404') || err.message.includes('Not Found')) {
        showError('Ticket not found. It may have been deleted.');
      } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        showError('Please log in to view ticket details.');
      } else {
        showError('Failed to load ticket details. Please try again.');
      }
    }
  };

  const priorityColors = {
    low: theme === 'dark' ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800',
    medium: theme === 'dark' ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
    high: theme === 'dark' ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
  };

  const statusColors = {
    open: theme === 'dark' ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800',
    closed: theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800',
    pending: theme === 'dark' ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {/* Backend Status Warning */}
      {!backendAvailable && (
        <div className={`${theme === 'dark' ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'} rounded-lg border p-4`}>
          <div className="flex items-center gap-2">
            <div className="text-red-500">⚠️</div>
            <div>
              <h4 className={`font-medium ${theme === 'dark' ? 'text-red-400' : 'text-red-800'}`}>
                Support System Unavailable
              </h4>
              <p className={`text-sm ${theme === 'dark' ? 'text-red-300' : 'text-red-600'}`}>
                The support system is temporarily unavailable. This may be due to server maintenance or connectivity issues.
              </p>
              <button
                onClick={loadTickets}
                className={`mt-2 text-sm underline ${theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-500'}`}
              >
                Try reconnecting
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create New Ticket Form */}
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6`}>
        <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Create New Support Ticket
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Subject *
            </label>
            <input
              type="text"
              value={newTicket.subject}
              onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                theme === 'dark' 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="Brief description of your issue..."
              required
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Priority
            </label>
            <select
              value={newTicket.priority}
              onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                theme === 'dark' 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="low">Low - General inquiry</option>
              <option value="medium">Medium - Feature request or bug</option>
              <option value="high">High - Account or payment issue</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Message *
            </label>
            <textarea
              value={newTicket.message}
              onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                theme === 'dark' 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="Please describe your issue in detail..."
              required
            />
          </div>

          <div className="flex items-center justify-between">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              We typically respond within 24-48 hours
            </p>
            <LoadingButton
              type="submit"
              loading={loading}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium"
            >
              Submit Ticket
            </LoadingButton>
          </div>
        </form>
      </div>

      {/* Tickets List */}
      <div className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border`}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Your Support Tickets
          </h3>
        </div>

        <div className="p-6">
          {tickets.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🎫</div>
              <p className={`text-lg font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                No tickets yet
              </p>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Create your first support ticket above
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {tickets.map((ticket) => (
                <div 
                  key={ticket.id || ticket._id} 
                  className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                    theme === 'dark' 
                      ? 'border-gray-600 bg-gray-750 hover:bg-gray-700' 
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                  onClick={() => handleTicketClick(ticket)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {ticket.subject}
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[ticket.priority || 'medium']}`}>
                        {ticket.priority || 'Medium'}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[ticket.status || 'open']}`}>
                        {ticket.status || 'Open'}
                      </span>
                    </div>
                  </div>
                  
                  <p className={`text-sm mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {ticket.message.length > 150 ? `${ticket.message.substring(0, 150)}...` : ticket.message}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                      {new Date(ticket.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    {ticket.admin_response && (
                      <span className={`text-xs px-2 py-1 rounded ${theme === 'dark' ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                        Response received
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ticket Detail Modal */}
      {showTicketDetail && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {selectedTicket.subject}
                </h3>
                <button
                  onClick={() => setShowTicketDetail(false)}
                  className={`text-gray-500 hover:text-gray-700 ${theme === 'dark' ? 'hover:text-gray-300' : ''}`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="flex items-center gap-4 mt-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${priorityColors[selectedTicket.priority || 'medium']}`}>
                  {selectedTicket.priority || 'Medium'} Priority
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[selectedTicket.status || 'open']}`}>
                  {selectedTicket.status || 'Open'}
                </span>
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {new Date(selectedTicket.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
            
            <div className="p-6">
              <div className="mb-6">
                <h4 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Your Message:
                </h4>
                <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {selectedTicket.message}
                </p>
              </div>
              
              {selectedTicket.admin_response && (
                <div className={`border-l-4 border-purple-500 pl-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-purple-50'} p-4 rounded-r-lg`}>
                  <h4 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Support Response:
                  </h4>
                  <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {selectedTicket.admin_response}
                  </p>
                  {selectedTicket.updated_at && selectedTicket.updated_at !== selectedTicket.created_at && (
                    <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                      Responded on {new Date(selectedTicket.updated_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              )}
              
              {selectedTicket.admin_response && selectedTicket.status === 'closed' && (
                <div className={`border border-dashed rounded-lg p-4 text-center ${theme === 'dark' ? 'border-green-600 bg-green-900/20' : 'border-green-300 bg-green-50'}`}>
                  <p className={`text-sm ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                    ✅ This ticket has been resolved and closed.
                  </p>
                </div>
              )}
              
              {!selectedTicket.admin_response && selectedTicket.status === 'open' && (
                <div className={`border border-dashed rounded-lg p-4 text-center ${theme === 'dark' ? 'border-gray-600 bg-gray-750' : 'border-gray-300 bg-gray-50'}`}>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    We're working on your request. You'll receive a response within 24-48 hours.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}
